
import React, { forwardRef, useEffect } from 'react'

import { AlignJustifyIcon, ListChecksIcon } from 'lucide-react'

import {
    Stack,
    Tooltip,
} from '@mui/material'

import { Button, fieldBorderColor } from '@repo/ui'
import { SEARCH_SCAN_TARGET_KEY, useScanRouting } from 'contexts'
import type { ScanFilterEntity } from 'services/scanResolveApi'
import {
    Filters,
    Sort,
    Search,
    LayoutOptions,
} from 'components'
// The request shape only — `Filters` itself still comes through the barrel above.
import type { FilterOpenRequest } from '../filters/Filters'

export type { FilterOpenRequest }
// Direct file import, NOT the `components` barrel — this file is part of that barrel, so importing
// back through it is a cycle. It is not theoretical: routed through the barrel, an HMR update of
// this file threw `ReferenceError: ScanTargetButton is not defined` and took the whole toolbar out
// via the error boundary, because the binding had not been initialised when this module evaluated.
import { ScanTargetButton } from '../ScanTargetButton'
// Same-directory file import for the same cycle reason as above.
import { toolbarContainerSx } from './toolbarCollapse'

export interface ActionLineProps {
    loading?: boolean
    apiRef?: any
    showSearch?: boolean
    // Independently control the search field's disabled state. Defaults to
    // enabled — a search box must never disable while a query it triggered is
    // loading (that interrupts typing). Pass true only to hard-disable search.
    searchDisabled?: boolean
    search?: string
    onSearchChange?: (value: string) => void
    /**
     * Show the scan control immediately to the right of the search input — OUTSIDE it, as its
     * own button (VRG-265, corrected; it used to be an end-adornment inside the field).
     *
     * It is a TARGET SELECTOR, exactly like the ones on the filter drawer's fields, and it shares
     * their single target slot: aiming it un-aims whichever filter field was lit, and aiming a
     * filter field un-aims this. Lit means "the next tag scanned lands in this search box". It is
     * also where a session is started (nothing can be aimed without one) and ended. Opt-in per
     * surface, because a scan only means something on a list that tags can narrow.
     */
    showScan?: boolean
    /**
     * What a tag scanned from THIS toolbar resolves to. Defaults to 'certificate' — the
     * certificates list, where this control first shipped and whose search matches tag id.
     * The internal Tags list passes 'tag': there the scan IS the row being searched for, so
     * it resolves with no server dependency (see projectResolution).
     */
    scanEntity?: ScanFilterEntity
    serverSide?: boolean
    rightComponents?: React.ReactNode
    showBulkActions?: boolean
    showRowSelection?: boolean
    bulkActionsToggle?: () => void
    showSort?: boolean
    sort?: any[]
    /** `sort` is still the table's default order, so the control renders un-applied. */
    sortIsDefault?: boolean
    onSortChange?: (col: any, direction?: any, override?: any) => void
    showFilters?: boolean
    filters?: any[]
    onFiltersChange?: (value: any[]) => void
    extraFilters?: React.ReactNode
    /** Forwarded to `Filters` — see its `extraFiltersPosition` note. */
    extraFiltersPosition?: 'before' | 'after'
    onFiltersApply?: () => void
    extraFiltersDirty?: boolean
    onClearExtraFilters?: () => void
    /**
     * Open the filter drawer at a named field, from outside the toolbar.
     *
     * Additive and default-off: absent, this changes nothing for the ~30 pages that
     * render an `ActionLine`. Its one consumer today is the Analytics page's
     * applied-filter chip row, where each chip reopens the dimension it stands for.
     * See `FilterOpenRequest` for why the request carries a token.
     */
    filterOpenRequest?: FilterOpenRequest | null
    /**
     * Forwarded to `Filters` — which staged filters its applied-count BADGE counts.
     * Additive and default-off; see that component's own `countFilter` doc for why the
     * Analytics page needs it and why nothing else does.
     */
    countFilter?: (filter: any) => boolean
    showLayoutOptions?: boolean
    layout?: string
    density?: string
    columns?: any[],
    rows?: any[],
    visibilityModel?: object,
    onResetColumns?: () => void,
    sx?: object
    // Slot rendered in the right group immediately before the Filter button
    // (e.g. the saved-views dropdown).
    beforeFiltersComponents?: React.ReactNode
    /**
     * Slot rendered in the right group immediately AFTER Filter — the same position the
     * built-in `showLayoutOptions` / `LayoutOptions` occupies (after Filter, after Sort).
     *
     * Additive and default-off, so every existing consumer (~30 pages) is byte-identical
     * without it. Its one consumer today is the Analytics page's own "Layout" control
     * (`AnalyticsCustomizeButton`, VERA-260) — a page-specific layout toggle that cannot be
     * `LayoutOptions` itself (it edits the dashboard's card grid, not a DataGrid's column
     * visibility/order/density), but that still belongs in the SAME trailing position every
     * other page's Layout control renders in. It used to ride in
     * `beforeFiltersComponents` alongside Views, which put it to the LEFT of Filter — the one
     * page whose Layout control did not sit where Layout sits everywhere else.
     *
     * No bespoke spacing needed here: this slot is a sibling of `Filters` / `Sort` /
     * `LayoutOptions` inside the same right-group `Stack`, so it inherits that Stack's own
     * `spacing={3}` (12px) — the same gap every trailing toolbar control already uses.
     */
    afterFiltersComponents?: React.ReactNode
    /**
     * Slot rendered at the END of the LEFT group — after the search field and its scan
     * button, before the flexible gap that pushes the right-hand button group over.
     *
     * Additive and default-off, so it changes nothing for the ~30 pages that already
     * render an `ActionLine`. Its one consumer today is the Analytics page's date-range
     * picker, which used to sit in a row of its own above this toolbar.
     *
     * ⚠️ THE LEFT GROUP, NOT `rightComponents`, AND THE REASON IS THE 49px BAND. The right
     * group is BUTTONS; this slot is for a control that is a FIELD. §29 fixes the toolbar
     * band at 49px precisely so a field and the controls beside it share one height, and
     * `Search` is the field that band was sized against — so a second field belongs next to
     * it, on the same side, rather than among the buttons where it would be the one item
     * that is not a button.
     *
     * 🚨 IT WAS A *BEFORE*-SEARCH SLOT FOR ONE COMMIT, and the argument for that was that a
     * scoping control should lead: the date range decides what every number on Analytics was
     * measured over, search narrows within it, so left-to-right would read scope → narrow →
     * act. Overruled by the user, and the counter-argument is the stronger one — SEARCH IS
     * THE FIXED LANDMARK OF THIS TOOLBAR. It is the first control on ~30 pages, at the same
     * x on every one of them, and it is what the eye goes to on arrival. Putting a
     * variable-width field ahead of it makes the one predictable element in the row start at
     * a position that depends on which preset the date happens to be showing (~280px against
     * ~420px timed). A slot that TRAILS the landmark costs nothing to any page that does not
     * fill it and cannot move the one that does.
     *
     * Kept as a single after-slot rather than an after- beside the old before-: nothing but
     * Analytics ever adopted the before-slot, so a second slot would be an abstraction with
     * no consumer, kept for symmetry alone.
     */
    afterSearchComponents?: React.ReactNode
}

export const ActionLine = forwardRef<HTMLDivElement, ActionLineProps>((props, ref) => {
    const {
        loading = false,
        apiRef,
        showSearch = true,
        searchDisabled,
        search,
        onSearchChange,
        showScan = false,
        scanEntity = 'certificate',
        serverSide,
        rightComponents,
        showRowSelection = false,
        showBulkActions = true,
        bulkActionsToggle,
        showSort,
        sort,
        sortIsDefault,
        onSortChange,
        showFilters = true,
        filters,
        onFiltersChange,
        extraFilters,
        extraFiltersPosition,
        onFiltersApply,
        extraFiltersDirty,
        onClearExtraFilters,
        filterOpenRequest,
        countFilter,
        showLayoutOptions,
        layout,
        density,
        columns,
        visibilityModel,
        onResetColumns,
        rows,
        sx,
        beforeFiltersComponents,
        afterFiltersComponents,
        afterSearchComponents,
    } = props
    // VRG-265 — the session lives in `ScanRoutingProvider`, above the page, because it outlives
    // both the pairing modal and any one filter field. This button opens the pairing step, aims
    // scans at this search box, and ends the session; it does not own the session.
    const scanning = useScanRouting()

    /**
     * WHERE A SCAN LANDS while this control is lit: the tag id goes into the search box.
     *
     * The certificates search matches on tag id server-side, so the list narrows to the exact
     * item just tapped — and the operator can SEE what happened, because the text is sitting in
     * the field where they can read it, clear it, or type over it. That visibility is the whole
     * argument for routing into an existing control rather than an invisible side-list.
     *
     * One value at a time, because a search box holds one string: a second scan replaces the
     * first. Narrowing to several items at once is what the filter fields are for.
     */
    const scannedForSearch = scanning.scannedFor(SEARCH_SCAN_TARGET_KEY)
    const latestScan = scannedForSearch[scannedForSearch.length - 1]
    useEffect(() => {
        if (!latestScan) return
        onSearchChange?.(latestScan.tagId)
        // Hand it to the input and drop it here. The input is now the record of what was scanned;
        // a copy left in the routing layer would only re-apply itself on the next render.
        scanning.clearScanned(SEARCH_SCAN_TARGET_KEY)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestScan?.tagId])

    // Tell the routing layer this control is on screen, so a landing the operator can watch
    // happen is not ALSO announced as a toast (see `land` in ScanRoutingContext).
    useEffect(() => {
        if (!showScan) return
        return scanning.registerField(SEARCH_SCAN_TARGET_KEY)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showScan, scanning.registerField])

    /**
     * The scan control sits OUTSIDE the search input, as a separate adjacent button — the same
     * treatment the filter drawer's per-field target selector uses (`SearchFilter`), so the two
     * read as one control wherever they appear.
     *
     * It used to render as an end-adornment inside the field, after the clear "×". That was
     * wrong on four counts. An adornment acts on a FIELD'S VALUE — clear, reveal, submit — and
     * is momentary; this is a stateful MODE toggle that aims an ongoing scan session at a target
     * and stays lit until it is turned off. At the adornment's 16px it competes with the clear
     * "×" for the same corner. Its active state has to be legible without reading as field
     * validation, which anything drawn inside the input's border does. And with a desk reader
     * attached it is aiming a stream of taps, not filling in this input at all.
     *
     * The button itself — its three states, its wording, its glyph — lives in `ScanTargetButton`,
     * shared with the filter fields, so the toolbar and the drawer cannot drift into describing
     * the same session differently. This surface supplies only what is local to it: that the
     * target is the search box, and that a tag here resolves to a certificate.
     */
    const scanButton = showScan ? (
        <ScanTargetButton
            target={{
                kind: 'search',
                key: SEARCH_SCAN_TARGET_KEY,
                // The list this control appears on is the certificates list, and its search
                // matches on tag id — so a tag resolves to its certificate and the scan narrows
                // to that one item.
                entity: scanEntity,
                label: 'Search',
            }}
            destination="search"
        />
    ) : null
    // TODO: these all flash on load and shouldnt
    return (
        <>
        <Stack
        ref={ref}
        direction="row"
        spacing={3}
        pb={4}
        sx={[
            {
                // The toolbar owns BOTH its gaps, top and bottom (VERA-455). `pb` has always
                // been here; the 16px above it used to be applied by the shared `Table` at its
                // own call site, so a surface that renders an ActionLine WITHOUT a Table got
                // the bottom gap and not the top one. Measured on the Tags page: Shop (bare
                // ActionLine) sat 16px under the tab row where Orders (Table) sat at 32px —
                // two tabs of one page, visibly different. Moving it here rather than adding
                // `mt` at the Shop call site is what stops a third surface drifting: this is
                // the same fix shape as `Header` owning its action-group gap (§29).
                //
                // First in the array, so a caller's `sx` still overrides it.
                mt: 4,
                // THIS ROW IS THE QUERY CONTAINER its controls' label-collapse rules are written
                // against (VRG-535 — see `toolbarCollapse.ts` for the whole mechanism). Declared
                // HERE rather than by each caller so every one of the ~32 toolbars in the app is a
                // container without doing anything, and so there can only ever be one container
                // NAME on this element: Analytics used to declare its own via the `sx` prop, and
                // two names on one element would mean whichever `sx` landed last silently won.
                //
                // Deliberately ABOVE the caller `sx` spread, like `mt`, so a surface can still opt
                // out — though nothing does, and a surface that did would lose label collapse for
                // every control in its row rather than gaining anything.
                ...toolbarContainerSx,
                // WRAP RATHER THAN CLIP. This row's controls are all sized by their labels, and
                // the search field carries a hard `minWidth: 300`, so nothing in it can yield —
                // when the row does not fit, the overflow comes off the right end and the last
                // button is cut in half. On Settings that is exactly what happened: the sub-nav
                // takes ~280px, leaving the toolbar 789px for its natural 852px, and the 63px
                // difference ate the Layout button. It was not even scrollable — the settings
                // scroller sets `overflowX: 'hidden'` on purpose, so the overflow was destroyed.
                //
                // Wrapping is chosen over shrinking the search field because it cannot regress
                // anything: no control changes width at any viewport, and on every page that
                // already fits, this line does nothing at all. Two attempts at the width-
                // negotiation version both misfired — the field's `sx` lands on the input root,
                // whose parent is a plain block Box, so `flex`/`flex-basis` there is inert and
                // silently falls back to content width, which shrank the field on the WIDE pages
                // that had no problem and then overlapped it with the Download button.
                //
                // ⚠️ STILL THE FALLBACK, NOT THE FIRST LINE OF DEFENCE (VRG-535). Wrapping is what
                // this row does once there is nothing left to shed, and it was reaching that point
                // far too early: at a 1280 viewport with the sidebar expanded this container is
                // 943px against the 1029px the five labelled buttons and the search field need, so
                // `/certificates` wrapped on the reference desktop width. The right-hand controls
                // now drop their LABELS one at a time before it gets here (`toolbarCollapse.ts`),
                // which buys ~256px. Do not remove this line on the strength of that — the ladder
                // has a floor, and below it wrap is still the only correct answer.
                flexWrap: 'wrap',
                rowGap: 3,
                // The table's action row is BORDERLESS, matching dark mode — where the tertiary
                // variant's border resolves to primary[50], the same value as the surface behind
                // it, so these buttons have effectively never had a visible border there. Light
                // was the odd one out.
                //
                // Scoped to the container rather than done by editing the variant: `tertiary` has
                // 39 usages including modals and avatar fields, which legitimately keep their
                // borders. Doing it here also means the next control added to this row inherits
                // it, instead of being the one button that still has an outline.
                // NO fill override here any more (2026-08-12). Search used to be forced WHITE in
                // light, because the field fill was #f6f6f6 and read as a heavy grey box against
                // the page. Both halves of that have since changed: the shared fill is #fafafa,
                // and the secondary buttons in this row now use that SAME token — so the search
                // box and the controls beside it are one surface, which is the point. Forcing
                // white here would put the field back to being the one different colour in the
                // row, which is the complaint this rule originally existed to answer.
                //
                // The border stays and is still doing real work: at these fills the step against
                // the page is far below the ~1.3:1 a surface needs to define itself, so the edge
                // is what draws the control. That applies equally to the buttons, which is why
                // they carry the same border rather than none.
                '& .MuiInputBase-root': {
                    borderColor: theme => fieldBorderColor(theme),
                },
                // NO per-button border override here any more (2026-08-12). This row's controls
                // are `secondary`, and secondary owns a `primary[100]` border — the same token the
                // table and every field use — so the row takes its edge from the variant instead
                // of a container rule fighting it.
                //
                // What was here forced `1px solid transparent` on every button, from when these
                // were `tertiary`: tertiary's border equals its own background, invisible in dark
                // but a real grey in light, so the row was flattened to match dark. Changing the
                // variant fixes the cause and leaves tertiary borderless for the surfaces that
                // want it. Border WIDTH is still 1px in every state, so the flash and
                // layout-shift that once justified `transparent` over `none` cannot return.
                //
                // Filter is wrapped in a MuiButtonGroup, which emits its own divider styling —
                // `.MuiButtonGroup-firstButton { border-color: rgb(123, 31, 162) }`, MUI's DEFAULT
                // palette purple, since nothing in this theme owns that slot. It sets only the
                // border-COLOR, so it does not change the width; it just repaints the edge. That
                // was previously neutralised to `transparent`, which is what left Filter as the
                // one button in the row with NO outline once the others gained one. Point it at
                // the row's own token instead. Three classes deep, so it outranks ButtonGroup's
                // two-class rule without !important.
                '& .MuiButtonGroup-root .MuiButtonGroup-firstButton, & .MuiButtonGroup-root .MuiButtonGroup-middleButton, & .MuiButtonGroup-root .MuiButtonGroup-lastButton':
                    {
                        borderColor: theme => theme.palette.primary[100],
                    },
                // ...and give the HOVER edge back (2026-10-01). The rule above is three classes
                // deep so it outranks ButtonGroup's purple, which also means it outranked the
                // secondary variant's own `&:hover` border (`primary[200]`, Button.tsx) — so
                // Filter was the one button in the row whose edge did not move on hover, while
                // Download, Sort and Layout (plain secondaries, no ButtonGroup) did. Same token
                // as the variant's hover, one class deeper than the rest rule. Disabled buttons
                // keep the rest edge, as the variant does (it attaches no hover when disabled).
                //
                // The FILL too. Filter's ButtonGroup is `variant="contained" color="secondary"`,
                // and MUI's grouped-contained hover painted `secondary.dark` (measured
                // rgb(231,231,231) light) where the secondary variant's hover is `primary[50]`
                // (rgb(246,246,246)) on Download / Sort / Layout. Same token as the variant.
                '& .MuiButtonGroup-root .MuiButtonGroup-firstButton:not(.Mui-disabled):hover, & .MuiButtonGroup-root .MuiButtonGroup-middleButton:not(.Mui-disabled):hover, & .MuiButtonGroup-root .MuiButtonGroup-lastButton:not(.Mui-disabled):hover':
                    {
                        borderColor: theme => theme.palette.primary[200],
                        background: theme => theme.palette.primary[50],
                    },
                // NO divider rule here. Adding one at this level put a right border on FILTER, which
                // is also wrapped in a ButtonGroup but is a single button — a divider with nothing to
                // divide. A segment divider belongs to the specific control that has segments, so
                // Sort draws its own (see Sort.tsx, gated on `sort.length > 0`).
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        justifyContent="space-between"
        alignItems="center">
            <Stack
            direction="row"
            justifyContent="left"
            alignItems="center"
            spacing={3}
            /**
             * WRAPS INTERNALLY, for the same reason the parent row does (see `flexWrap`
             * above): nothing in this group can yield — `Search` carries a hard
             * `minWidth: 300` and `afterSearchComponents` is sized by its own content — so
             * without this the overflow comes off the right end and is clipped rather than
             * reflowed. Inert on every page that does not fill the slot, since search + its
             * scan button is 366px and fits at every viewport this app supports.
             *
             * `useFlexGap` so `spacing` becomes a real `gap` rather than margins on the
             * children; at one line the two are identical (12px), but a margin-based gap is
             * applied to the wrong edge once a child wraps onto a second line.
             */
            useFlexGap
            flexWrap="wrap"
            rowGap={3}
            >
            {/* TODO: combine these two into a dropdown meny first for bul action and add Bulk edit as well: https://mui.com/x/react-data-grid/recipes-editing/#bulk-editing */}
            {showBulkActions && (
                <Tooltip title="Bulk actions" enterDelay={600}>
                    <Stack>
                        <Button
                        variant="secondary"
                        size="large"
                        disabled={loading}
                        onClick={bulkActionsToggle}
                        // The one BORDERED control in an otherwise borderless row, and it stays
                        // put: its position is functional, aligning with the bulk-action checkbox
                        // column in the table below, so it cannot move into the right-hand button
                        // group without breaking that alignment.
                        //
                        // Given it must sit immediately beside the Search field, it takes Search's
                        // border rather than the row's borderless treatment — same token via
                        // fieldBorderColor, so the two adjacent edges cannot drift apart. Without
                        // it, a bordered input and an unbordered button sat flush against each
                        // other and read as a mismatched pair.
                        data-bordered
                        sx={theme => ({
                            border: `1px solid ${fieldBorderColor(theme)}`,
                            '&:hover': { border: `1px solid ${fieldBorderColor(theme)}` },
                        })}
                        >
                            {showRowSelection ? <AlignJustifyIcon strokeWidth={1}/> : <ListChecksIcon strokeWidth={1}/>}
                        </Button>
                    </Stack>
                </Tooltip>
            )}
            {showSearch && (
                // Field + its scan button are one unit, paired at the same 8px gap the filter
                // drawer's field/scan row uses; the 12px `spacing` of the row above is the gap
                // BETWEEN units (bulk actions ↔ this one), not inside one.
                <Stack direction="row" spacing={2} alignItems="center">
                    <Search
                        apiRef={apiRef}
                        value={search}
                        onChange={onSearchChange}
                        serverSide={serverSide}
                        disabled={searchDisabled ?? false}
                    />
                    {scanButton}
                </Stack>
            )}
            {/* Trailing field slot — see `afterSearchComponents` for why a FIELD joins the
                left group rather than the button group on the right, and for why it trails
                search rather than leading it. */}
            {afterSearchComponents}
            </Stack>
            <Stack
                direction="row"
                justifyContent="right"
                alignItems="center"
                spacing={3}
                // Keeps this group hard right on its own line once the row wraps (see `flexWrap`
                // on the parent). A wrapped line holds ONE item, and the parent's
                // `justifyContent: space-between` puts a single item at flex-start — so the
                // button group jumped to the LEFT edge the moment it wrapped, which reads as a
                // bug rather than a reflow.
                //
                // `flexGrow`, NOT `ml: 'auto'`: MUI's `spacing` prop generates a
                // `& > :not(style) ~ :not(style) { margin-left: 12px }` rule on the parent, which
                // out-specifies a plain `ml` on the child — the auto margin was silently replaced
                // by 12px and the group stayed left. Growing to fill the line instead lets this
                // group's own `justifyContent="right"` do the alignment, which nothing overrides.
                // Unwrapped the result is identical to space-between.
                sx={{flexGrow: 1}}
            >
                {rightComponents && (
                    /*
                      A ROW, and the direction is load-bearing. This was a bare `<Stack>`,
                      which defaults to flex-direction COLUMN — so any caller passing more
                      than one child got them stacked vertically inside a 49px toolbar
                      band that is a row by construction (§29).

                      Invisible until someone passed two children, which is why a wrong
                      default sat here unnoticed: every existing caller passes a single
                      element, and a single element renders identically either way. The
                      market Claims tab was the first to pass two (a status filter, then
                      the Table's own Download) and got the filter at y=173 with Download
                      at y=225, same left edge, overlapping the table header.

                      Safe to change for exactly that reason — audited before doing it: no
                      direct `<ActionLine>` caller in the app passes a fragment to this
                      prop, so nothing currently relies on the column behaviour.

                      12px gap, matching the button-group rule (§29) and the spacing the
                      toolbar's own Filter/Sort/Layout already use.

                      Adding these props costs one more "Excessive complexity comparing
                      types 'Theme' and 'Theme'" in this file, which already carries seven
                      — the repo has TWO MUI copies installed (React 18 and 19) and every
                      themed component multiplies that comparison. Writing the same layout
                      in `sx` was tried and does NOT avoid it. Accepted rather than worked
                      around: it is dependency-duplication noise, not a real type error,
                      and the platform total is unchanged at 1024.
                    */
                    <Stack direction="row" alignItems="center" spacing={3}>
                        {rightComponents}
                    </Stack>
                )}
                {beforeFiltersComponents}
                {showFilters && (
                    // TODO : prvent filter flashing badge on every change
                    <Filters apiRef={apiRef} filters={filters} columns={columns} rows={rows} onChange={onFiltersChange} disabled={loading} extraFilters={extraFilters} extraFiltersPosition={extraFiltersPosition} onApply={onFiltersApply} extraFiltersDirty={extraFiltersDirty} onClearExtraFilters={onClearExtraFilters} openRequest={filterOpenRequest} countFilter={countFilter}/>
                )}
                {/* Immediately after Filter — the same position `showLayoutOptions` occupies
                    for the pages that use the built-in Layout control. See the prop's own
                    doc for why this exists (a page-specific Layout control, e.g. Analytics'
                    dashboard-card layout, that isn't a DataGrid's `LayoutOptions`). */}
                {afterFiltersComponents}
                {showSort && (
                    <Sort apiRef={apiRef} sort={sort} isDefault={sortIsDefault} columns={columns} onChange={onSortChange} disabled={loading}/>
                )}
                {showLayoutOptions && (
                    <>
                        <LayoutOptions
                            apiRef={apiRef}
                            layout={layout}
                            density={density}
                            columns={columns}
                            visibilityModel={visibilityModel}
                            onResetColumns={onResetColumns}
                            disabled={loading}
                        />
                    </>
                )}
            </Stack>
        </Stack>
        </>
    )
})

ActionLine.displayName = 'ActionLine'